"use client"

import { useState, useEffect, useRef, memo, useMemo, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material"
import { ArrowBack, Add, Warning } from "@mui/icons-material"
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import LoadingButton from "../../components/common/LoadingButton"
import { getCreditsSummary, addCredits, deductCredits, getCombinedCreditHistoryPaginated } from "../../services/credit.service"
import { getCustomer, reactivateCustomer } from "../../services/customer.service"
// Add this import
import { getSubscription } from "../../services/subscription.service"

// Isolated credit history component - only re-renders when data changes
const CreditHistoryTable = memo(({ 
  creditHistory, 
  loading, 
  isFetching, 
  showLoadMore, 
  hasMore, 
  onFetchNextPage, 
  onLoadMore
}) => {
  const SCROLLABLE_TABLE_ID = 'credit-history-scrollable'

  if (creditHistory.length === 0 && !loading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        No credit activity found
      </Typography>
    )
  }

  return (
    <Box sx={{ height: "calc(100vh - 350px)", overflow: "hidden" }}>
      <Box 
        id={SCROLLABLE_TABLE_ID}
        sx={{ 
          height: "100%", 
          overflow: "auto",
          position: "relative"
        }}
      >
        <InfiniteScroll
          dataLength={creditHistory.length}
          next={onFetchNextPage}
          hasMore={!showLoadMore}
          scrollableTarget={SCROLLABLE_TABLE_ID}
          scrollThreshold={0.98}
        >
          {creditHistory.map((item, index) => (
            <Box
              key={item._id}
              sx={{ mb: 2, pb: 2, borderBottom: index < creditHistory.length - 1 ? "1px solid #eee" : "none" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  color={item.transactionType === "debit" ? "error.main" : "success.main"}
                >
                  {item.transactionType === "debit" ? "-" : "+"}
                  {Math.abs(item.amount)} credits
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.createdAt).toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Type: {item.type || "N/A"}
              </Typography>
              {item.description && (
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {item.transactionType === "credit" ? "Credit Added" : "Credit Used"}
              </Typography>
            </Box>
          ))}
          
          {/* Load More Section */}
          {isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          )}
          
          {showLoadMore && !isFetching && (
            <Box sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
              <b>No more credit history to load.</b>
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={onLoadMore}
                  sx={{ borderRadius: 2 }}
                >
                  Load More
                </Button>
              </Box>
            </Box>
          )}
        </InfiniteScroll>
      </Box>
    </Box>
  )
})

const CustomerCredits = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [credits, setCredits] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deductDialogOpen, setDeductDialogOpen] = useState(false)
  const [reactivateCustomerDialogOpen, setReactivateCustomerDialogOpen] = useState(false)
  const [formData, setFormData] = useState({
    amount: "",
    description: "",
    type: "manual",
  })
  const [creditHistory, setCreditHistory] = useState([])
  // Add this state
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  // Pagination states for credit history
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [noMoreData, setNoMoreData] = useState(false)
  const [showLoadMore, setShowLoadMore] = useState(false)
  const limit = 20

  const organizationId = selectedOrganization?._id || user?.organizationId

  useEffect(() => {
    const orgId = selectedOrganization?._id || user?.organizationId
    if (orgId && id) {
      fetchDataWithOrgId(orgId)
    }
  }, [selectedOrganization, user, id])


  const fetchCreditHistoryPaginated = async (pageToFetch = 1, reset = false) => {
    setIsFetching(true)
    try {
      const orgId = selectedOrganization?._id || user?.organizationId
      const response = await getCombinedCreditHistoryPaginated(id, orgId, pageToFetch, limit)
      
      let newHistory = response.data || []
      setTotalCount(response.count || 0)
      
      if (reset) {
        setCreditHistory(newHistory)
        setNoMoreData(false)
        setShowLoadMore(false)
      } else {
        setCreditHistory((prev) => {
          const existingIds = prev.map((item) => item._id)
          newHistory = newHistory.filter((item) => !existingIds.includes(item._id))
          return [...prev, ...newHistory]
        })
      }
      
      if (newHistory.length < limit) {
        setNoMoreData(true)
        setShowLoadMore(true)
      } else {
        setNoMoreData(false)
        setShowLoadMore(false)
      }
      setHasMore(!showLoadMore)
    } catch (error) {
      console.error("Failed to fetch credit history:", error)
      showNotification("Failed to fetch credit history", "error")
    } finally {
      setIsFetching(false)
    }
  }

  const fetchDataWithOrgId = async (orgId) => {
    setLoading(true)
    try {
      // Fetch customer
      const customerResponse = await getCustomer(id)
      setCustomer(customerResponse.data)

      // Fetch credit summary (this gives us the total balance info without the list)
      const creditsResponse = await getCreditsSummary(id, orgId)
      setCredits(creditsResponse.data)

      // Reset pagination and fetch first page of credit history
      setPage(1)
      setHasMore(true)
      await fetchCreditHistoryPaginated(1, true)

      // Check if customer has active subscription
      try {
        const subscriptionResponse = await getSubscription(id)
        console.log("Subscription response:", subscriptionResponse)

        // Check if subscription exists and has a valid status
        const subscription = subscriptionResponse.data
        const activeStatuses = [
          "active",
          "trial",
          "past_due",
          "grace_period",
          "dunning",
          "pending_activation",
          "pending_cancellation",
        ]

        setHasActiveSubscription(subscription && activeStatuses.includes(subscription.status))

        console.log("Has active subscription:", subscription && activeStatuses.includes(subscription.status))
      } catch (error) {
        console.error("Failed to fetch subscription:", error)
        setHasActiveSubscription(false)
      }
    } catch (error) {
      console.error("Failed to fetch data:", error)
      showNotification("Failed to fetch credit data", "error")
    } finally {
      setLoading(false)
    }
  }

  // Fix the handleAddCredits function
  const handleAddCredits = async () => {
    if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) {
      showNotification("Please enter a valid amount", "error")
      return
    }

    const orgId = selectedOrganization?._id || user?.organizationId
    if (!orgId) {
      showNotification("No organization selected", "error")
      return
    }

    setActionLoading(true)
    try {
      // Fix: Pass parameters directly instead of as a nested object
      await addCredits(
        id, // customerId
        Number(formData.amount),
        formData.type,
        formData.description,
        orgId,
      )
      showNotification("Credits added successfully", "success")
      setAddDialogOpen(false)
      setFormData({ amount: "", description: "", type: "manual" })
      // Refresh credit balance and history after adding credits
      const creditsResponse = await getCreditsSummary(id, orgId)
      setCredits(creditsResponse.data)
      setPage(1)
      setHasMore(true)
      await fetchCreditHistoryPaginated(1, true)
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to add credits", "error")
    } finally {
      setActionLoading(false)
    }
  }

  // Fix the handleDeductCredits function
  const handleDeductCredits = async () => {
    if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) {
      showNotification("Please enter a valid amount", "error")
      return
    }

    if (Number(formData.amount) > credits.availableCredits) {
      showNotification("Cannot deduct more credits than available", "error")
      return
    }

    const orgId = selectedOrganization?._id || user?.organizationId
    if (!orgId) {
      showNotification("No organization selected", "error")
      return
    }

    setActionLoading(true)
    try {
      // Fix: Pass parameters directly instead of as a nested object
      await deductCredits(
        id, // customerId
        Number(formData.amount),
        formData.type,
        formData.description,
        orgId,
      )
      showNotification("Credits deducted successfully", "success")
      setDeductDialogOpen(false)
      setFormData({ amount: "", description: "", type: "manual" })
      // Refresh credit balance and history after deducting credits
      const creditsResponse = await getCreditsSummary(id, orgId)
      setCredits(creditsResponse.data)
      setPage(1)
      setHasMore(true)
      await fetchCreditHistoryPaginated(1, true)
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to deduct credits", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivateCustomer = async () => {
    setActionLoading(true)
    try {
      await reactivateCustomer(id)
      showNotification("Customer reactivated successfully", "success")
      setReactivateCustomerDialogOpen(false)
      fetchDataWithOrgId(selectedOrganization?._id || user?.organizationId)
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to reactivate customer", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const fetchNextPage = useCallback(() => {
    if (isFetching || showLoadMore) return
    setPage((prevPage) => {
      let nextPage = prevPage
      if(totalCount >= limit) {
        nextPage = prevPage + 1
      }
      fetchCreditHistoryPaginated(nextPage, false)
      return nextPage
    })
  }, [isFetching, showLoadMore, totalCount, limit])

  const handleLoadMore = useCallback(() => {
    setShowLoadMore(false)
    setPage((prevPage) => {
      let nextPage = prevPage
      if(totalCount >= limit) {
        nextPage = prevPage + 1
      }
      fetchCreditHistoryPaginated(nextPage, false)
      return nextPage
    })
  }, [totalCount, limit])

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Update the UI to show subscription warning
  return (
    <Box>
      <PageHeader
        title={customer ? `${customer.name}'s Credits` : "Customer Credits"}
        subtitle="Manage customer credits"
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Customers", link: "/customers" },
          { text: customer ? customer.name : "Customer", link: customer ? `/customers/${id}` : "/customers" },
          { text: "Credits" },
        ]}
      />

      <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(`/customers/${id}`)} sx={{ mb: 3 }}>
        Back to Customer
      </Button>

      {customer && customer.status === "inactive" && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReactivateCustomerDialogOpen(true)}>
              Reactivate
            </Button>
          }
        >
          <Typography variant="subtitle2">This customer is inactive</Typography>
          <Typography variant="body2">
            Credit management is disabled for inactive customers. Reactivate the customer to manage credits.
          </Typography>
        </Alert>
      )}

      {/* Add this alert for subscription check */}
      {customer && customer.status === "active" && !hasActiveSubscription && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/subscriptions/customer/${id}`)}>
              Add Subscription
            </Button>
          }
        >
          <Typography variant="subtitle2">No active subscription</Typography>
          <Typography variant="body2">
            This customer doesn't have an active subscription. Credits can only be added to customers with active
            subscriptions.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Credit Balance
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {credits ? (
              <>
                <Typography variant="h3" gutterBottom>
                  {credits.availableCredits}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available credits
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total Credits: {credits.totalCredits}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Used Credits: {credits.totalUsage}
                </Typography>

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  {customer && customer.status === "inactive" ? (
                    <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Warning fontSize="small" />
                      Credit management is disabled for inactive customers
                    </Typography>
                  ) : !hasActiveSubscription ? (
                    <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Warning fontSize="small" />
                      Customer needs an active subscription to add credits
                    </Typography>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => {
                          setFormData({ amount: "", description: "", type: "manual" })
                          setAddDialogOpen(true)
                        }}
                      >
                        Add Credits
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                          setFormData({ amount: "", description: "", type: "manual" })
                          setDeductDialogOpen(true)
                        }}
                        disabled={!credits.availableCredits}
                      >
                        Deduct Credits
                      </Button>
                    </>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No credit information available for this customer.
                </Typography>
                {customer && customer.status === "inactive" ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Warning fontSize="small" />
                      Cannot manage credits for inactive customer
                    </Typography>
                    <Button variant="contained" color="primary" onClick={() => setReactivateCustomerDialogOpen(true)}>
                      Reactivate Customer
                    </Button>
                  </Box>
                ) : !hasActiveSubscription ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Warning fontSize="small" />
                      Customer needs an active subscription to add credits
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate(`/subscriptions/customer/${id}`)}
                    >
                      Add Subscription
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setFormData({ amount: "", description: "", type: "manual" })
                      setAddDialogOpen(true)
                    }}
                  >
                    Add Credits
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Credit Activity
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <CreditHistoryTable
              creditHistory={creditHistory}
              loading={loading}
              isFetching={isFetching}
              showLoadMore={showLoadMore}
              hasMore={hasMore}
              onFetchNextPage={fetchNextPage}
              onLoadMore={handleLoadMore}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Add Credits Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add Credits</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Add credits to {customer?.name}'s account.</DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            name="amount"
            label="Amount"
            type="number"
            fullWidth
            value={formData.amount}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="description"
            label="Description (optional)"
            type="text"
            fullWidth
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <InputLabel id="type-label">Type</InputLabel>
            <Select labelId="type-label" name="type" value={formData.type} label="Type" onChange={handleChange}>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="bonus">Bonus</MenuItem>
              <MenuItem value="refund">Refund</MenuItem>
              <MenuItem value="promotional">Promotional</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <LoadingButton onClick={handleAddCredits} loading={actionLoading} variant="contained">
            Add Credits
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Deduct Credits Dialog */}
      <Dialog open={deductDialogOpen} onClose={() => setDeductDialogOpen(false)}>
        <DialogTitle>Deduct Credits</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Deduct credits from {customer?.name}'s account. Available: {credits?.availableCredits || 0}
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            name="amount"
            label="Amount"
            type="number"
            fullWidth
            value={formData.amount}
            onChange={handleChange}
            sx={{ mb: 2 }}
            inputProps={{ max: credits?.availableCredits || 0 }}
          />

          <TextField
            margin="dense"
            name="description"
            label="Description (optional)"
            type="text"
            fullWidth
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <InputLabel id="type-label">Type</InputLabel>
            <Select labelId="type-label" name="type" value={formData.type} label="Type" onChange={handleChange}>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="adjustment">Adjustment</MenuItem>
              <MenuItem value="expiration">Expiration</MenuItem>
              <MenuItem value="usage">Usage</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeductDialogOpen(false)}>Cancel</Button>
          <LoadingButton onClick={handleDeductCredits} loading={actionLoading} variant="contained" color="warning">
            Deduct Credits
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reactivate Customer Dialog */}
      <Dialog open={reactivateCustomerDialogOpen} onClose={() => setReactivateCustomerDialogOpen(false)}>
        <DialogTitle>Reactivate Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reactivate this customer? This will allow you to manage credits and subscriptions
            for this customer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateCustomerDialogOpen(false)}>Cancel</Button>
          <LoadingButton onClick={handleReactivateCustomer} loading={actionLoading} variant="contained" color="success">
            Reactivate Customer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerCredits

